import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';

const TeleCallingPaymentManagement = () => {
  const [records, setRecords] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const { user } = useContext(AuthContext);

  const [form, setForm] = useState({
    recipientType: '',
    recipient: '',
    callResponse: '',
    remarks: '',
    reminders: { message: '', date: '' },
    createdBy: user?.user?._id,
  });

  // For searching records
  const [searchByDealer, setSearchByDealer] = useState('');

  // Fetch all records, dealers, and customers
  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/tele-calling-payment/${user.user._id}`
        );
        const today = new Date().toISOString().split('T')[0];
        const filteredRecords = response.data.filter(
          (record) => record.createdAt.split('T')[0] === today
        );
        setRecords(filteredRecords);
      } catch (error) {
        Swal.fire('Error', 'Failed to fetch records', 'error');
      }
    };

    const fetchDealers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/dealers/all`
        );
        setDealers(response.data.dealers);
      } catch (error) {
        Swal.fire('Error', 'Failed to fetch dealers', 'error');
      }
    };

    const fetchCustomers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/customers/all`
        );
        setCustomers(response.data);
      } catch (error) {
        Swal.fire('Error', 'Failed to fetch customers', 'error');
      }
    };

    fetchRecords();
    fetchDealers();
    fetchCustomers();
  }, [user]);

  const validateForm = () => {
    const { recipientType, recipient, callResponse, remarks, reminders } = form;
    if (
      !recipientType ||
      !recipient ||
      !callResponse ||
      !remarks ||
      !reminders.message ||
      !reminders.date
    ) {
      Swal.fire('Warning', 'All fields are required', 'warning');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm({
      recipientType: '',
      recipient: '',
      callResponse: '',
      remarks: '',
      reminders: { message: '', date: '' },
      createdBy: user?.user?._id,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('reminders.')) {
      const field = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        reminders: {
          ...prev.reminders,
          [field]: value,
        },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/tele-calling-payment`,
        form
      );
      Swal.fire('Success', 'Record created successfully', 'success');
      // Re-fetch records
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/tele-calling-payment/${user.user._id}`
      );
      const today = new Date().toISOString().split('T')[0];
      const filteredRecords = response.data.filter(
        (record) => record.createdAt.split('T')[0] === today
      );
      setRecords(filteredRecords);
      resetForm();
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  // Filtered records based on the search value
  const filteredRecords = records.filter((record) => {
    if (!record.recipient) return false;
    const nameString =
      record.recipient.businessName || record.recipient.name || '';
    return nameString.toLowerCase().includes(searchByDealer.toLowerCase());
  });

  return (
    <div className="p-4 lg:p-6">
      {/* Page Title */}
      <h2 className="text-2xl font-bold mb-6">Tele-Calling Payment</h2>

      {/* Form Section */}
      <div className="bg-white rounded-lg shadow-md p-4 lg:p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Add New Record</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Recipient Type */}
          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Recipient Type*</label>
            <select
              name="recipientType"
              value={form.recipientType}
              onChange={(e) => {
                setForm({ ...form, recipient: '' });
                handleInputChange(e);
              }}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            >
              <option value="">Select Type</option>
              <option value="Customer">Customer</option>
              <option value="Dealer">Dealer</option>
            </select>
          </div>

          {/* Recipient */}
          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Recipient*</label>
            <Select
              value={form.recipient}
              onChange={(selectedOption) =>
                setForm({ ...form, recipient: selectedOption })
              }
              options={[
                ...(form.recipientType === 'Customer'
                  ? customers.map((customer) => ({
                      value: customer._id,
                      label: `${customer.name} (Phone: ${customer.phone}, ID: ${customer.customerCode})`,
                    }))
                  : []),
                ...(form.recipientType === 'Dealer'
                  ? dealers.map((dealer) => ({
                      value: dealer._id,
                      label: dealer.businessName,
                    }))
                  : []),
              ]}
              className="w-full text-sm"
              placeholder="Select Recipient"
            />
          </div>

          {/* Call Response */}
          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Call Response*</label>
            <select
              name="callResponse"
              value={form.callResponse}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            >
              <option value="">Select Response</option>
              <option value="RECEIVED">RECEIVED</option>
              <option value="NO REPLY">NO REPLY</option>
              <option value="BUSY">BUSY</option>
              <option value="NOT REACHABLE">NOT REACHABLE</option>
              <option value="SWITCH OFF">SWITCH OFF</option>
              <option value="NUMBER DOES NOT EXIST">
                NUMBER DOES NOT EXIST
              </option>
            </select>
          </div>

          {/* Remarks */}
          <div className="flex flex-col sm:col-span-2 lg:col-span-1">
            <label className="mb-1 text-gray-700">Remarks*</label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            />
          </div>

          {/* Reminder Message */}
          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Reminder Message*</label>
            <input
              type="text"
              name="reminders.message"
              value={form.reminders.message}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            />
          </div>

          {/* Reminder Date */}
          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Reminder Date*</label>
            <input
              type="date"
              name="reminders.date"
              value={form.reminders.date}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-6">
          <button
            onClick={handleSubmit}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md transition-colors"
          >
            Add Record
          </button>
        </div>
      </div>

      {/* Records Section in Grid Layout */}
      <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
        {/* Search Input */}
        <div className="mb-4">
          <input
            type="text"
            className="p-2 border rounded focus:ring focus:ring-blue-200 w-full sm:w-1/2"
            placeholder="Search by Dealer/Customer"
            value={searchByDealer}
            onChange={(e) => setSearchByDealer(e.target.value)}
          />
        </div>

        {/* Responsive Grid for Records */}
        {filteredRecords.length === 0 ? (
          <p className="text-gray-700">No records found for today.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {filteredRecords.map((record) => (
              <div
                key={record._id}
                className="border rounded-md p-4 hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-lg mb-2">
                  {record.recipient?.businessName ||
                    record.recipient?.name ||
                    'N/A'}
                </h4>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">Response:</span>{' '}
                  {record.callResponse}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">Remarks:</span> {record.remarks}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Status:</span>{' '}
                  {record.status || 'N/A'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeleCallingPaymentManagement;
